import { useCallback, useEffect, useRef, useState } from "react";

// An element's width, kept up to date. ref is a callback ref, so a container that mounts
// late (after a loading state) or again (after a table view) is still measured.
export function useMeasure(fallback = 720) {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(fallback);
  const ref = useCallback((node) => setEl(node), []);
  useEffect(() => {
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(260, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return { ref, el, width };
}

// Zoom and pan for the SVG maps (the missile map, the choropleths), on the map's container
// element (from useMeasure). The map's layers go
// in a <g transform={transform}>; marks that shouldn't grow (dots, labels) are placed at
// at(x, y) instead. The scroll wheel and trackpad pinches zoom at the pointer; drag pans,
// two fingers pinch, double-click zooms in (Shift + double-click out), and the buttons do
// the same. A click that ends a drag is swallowed, so panning never selects what's under
// the pointer.

const clampT = (t, w, h, maxK) => {
  const k = Math.min(maxK, Math.max(1, t.k));
  return { k, x: Math.min(0, Math.max(w - w * k, t.x)), y: Math.min(0, Math.max(h - h * k, t.y)) };
};

export function useZoomPan(el, { width, height, maxK = 12, resetKey }) {
  const [t, setT] = useState({ k: 1, x: 0, y: 0 });
  const pointers = useRef(new Map());
  const gesture = useRef(null); // { x, y, dist, moved }
  const swallow = useRef(false);
  const size = useRef({ width, height, maxK });
  size.current = { width, height, maxK };
  const now = useRef(t);
  now.current = t;

  const apply = useCallback((f) => setT(prev => clampT(f(prev), size.current.width, size.current.height, size.current.maxK)), []);
  const zoomAt = useCallback((px, py, factor) => apply(p => {
    const k = Math.min(size.current.maxK, Math.max(1, p.k * factor));
    return { k, x: px - (px - p.x) * (k / p.k), y: py - (py - p.y) * (k / p.k) };
  }), [apply]);
  const reset = useCallback(() => setT({ k: 1, x: 0, y: 0 }), []);
  const zoomBy = useCallback((factor) => zoomAt(size.current.width / 2, size.current.height / 2, factor), [zoomAt]);
  // Zoom so the box [x0, y0]–[x1, y1] (in the unzoomed map's pixels) fills `fill` of the view,
  // centred, as far as the zoom limit and the map's edges allow.
  const zoomTo = useCallback((x0, y0, x1, y1, fill = 0.9) => {
    const { width: w, height: h, maxK: mk } = size.current;
    const k = Math.min(mk, Math.max(1, fill * Math.min(w / Math.max(1, x1 - x0), h / Math.max(1, y1 - y0))));
    setT(clampT({ k, x: w / 2 - k * (x0 + x1) / 2, y: h / 2 - k * (y0 + y1) / 2 }, w, h, mk));
  }, []);

  // A new extent (or country) starts unzoomed; a resize keeps the view inside the map.
  useEffect(() => { reset(); }, [resetKey, reset]);
  useEffect(() => { apply(p => p); }, [width, height, apply]);

  // Wheel needs a non-passive listener to stop the page scrolling while zooming. The page
  // still scrolls when the wheel would zoom out past the whole map, and while the page is
  // already scrolling (a scroll down the page isn't caught when the map passes under the
  // pointer). Sideways swipes pan a zoomed map.
  useEffect(() => {
    if (!el) return undefined;
    let pageScrolled = 0;
    const onScroll = (e) => { if (!el.contains(e.target)) pageScrolled = Date.now(); };
    const onWheel = (e) => {
      const pinch = e.ctrlKey || e.metaKey;
      const zoomed = now.current.k > 1.001;
      if (!pinch && Date.now() - pageScrolled < 400) return;
      if (!pinch && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        if (!zoomed) return;
        e.preventDefault();
        apply(p => ({ ...p, x: p.x - e.deltaX }));
        return;
      }
      if (!pinch && e.deltaY > 0 && !zoomed) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0025)));
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => { document.removeEventListener("scroll", onScroll, { capture: true }); el.removeEventListener("wheel", onWheel); };
  }, [el, zoomAt, apply]);

  const local = (e) => { const r = el.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const centre = () => {
    const ps = [...pointers.current.values()];
    const x = ps.reduce((s, p) => s + p.x, 0) / ps.length, y = ps.reduce((s, p) => s + p.y, 0) / ps.length;
    const dist = ps.length > 1 ? Math.hypot(ps[0].x - ps[1].x, ps[0].y - ps[1].y) : 0;
    return { x, y, dist };
  };
  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    pointers.current.set(e.pointerId, local(e));
    gesture.current = { ...centre(), moved: gesture.current?.moved || false };
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, local(e));
    const g = gesture.current;
    const c = centre();
    const dx = c.x - g.x, dy = c.y - g.y;
    if (!g.moved && Math.hypot(dx, dy) < 4 && !(c.dist && g.dist && Math.abs(c.dist - g.dist) > 4)) return;
    if (!g.moved) { g.moved = true; try { el.setPointerCapture(e.pointerId); } catch { /* not capturable */ } }
    if (c.dist && g.dist) zoomAt(c.x, c.y, c.dist / g.dist);
    apply(p => ({ ...p, x: p.x + dx, y: p.y + dy }));
    gesture.current = { ...c, moved: true };
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (gesture.current?.moved) swallow.current = true;
    gesture.current = pointers.current.size ? { ...centre(), moved: true } : null;
  };
  const onClickCapture = (e) => {
    if (!swallow.current) return;
    swallow.current = false;
    e.stopPropagation();
    e.preventDefault();
  };
  const onDoubleClick = (e) => { const p = local(e); zoomAt(p.x, p.y, e.shiftKey ? 0.5 : 2); };

  return {
    t, reset, zoomBy, zoomTo, zoomed: t.k > 1.001,
    transform: `translate(${t.x} ${t.y}) scale(${t.k})`,
    at: (x, y) => [t.x + x * t.k, t.y + y * t.k],
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onClickCapture, onDoubleClick },
    // Vertical swipes still scroll the page until the map is zoomed in.
    style: { touchAction: t.k > 1.001 ? "none" : "pan-y", cursor: t.k > 1.001 ? (gesture.current?.moved ? "grabbing" : "grab") : "default", userSelect: "none" },
  };
}

// The +, − and reset buttons, over the map's top-right corner; `extra` is a button of the
// map's own (full screen, on the missile map) in a box of its own below them.
export function ZoomControls({ z, ui, extra = null }) {
  const { B, fs } = ui;
  const btn = { width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", background: B.bgCard, color: B.text, border: `1px solid ${B.border}`, cursor: "pointer", fontSize: fs(15), fontWeight: 700, lineHeight: 1, padding: 0 };
  const box = { display: "flex", flexDirection: "column", borderRadius: 7, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.18)" };
  return (
    <div data-noprint style={{ position: "absolute", top: 8, right: 8, display: "flex", flexDirection: "column", gap: 6, zIndex: 4 }}>
      <div style={box}>
        <button onClick={() => z.zoomBy(1.6)} aria-label="Zoom in" title="Zoom in" style={{ ...btn, borderBottom: "none" }}>+</button>
        <button onClick={() => z.zoomBy(1 / 1.6)} aria-label="Zoom out" title="Zoom out" disabled={!z.zoomed} style={{ ...btn, borderBottom: z.zoomed ? "none" : btn.border, opacity: z.zoomed ? 1 : 0.45, cursor: z.zoomed ? "pointer" : "default" }}>−</button>
        {z.zoomed && <button onClick={z.reset} aria-label="Reset the zoom" title="Reset the zoom" style={{ ...btn, fontSize: fs(13) }}>⟲</button>}
      </div>
      {extra && <div style={box}><button onClick={extra.onClick} aria-label={extra.label} title={extra.label} aria-pressed={extra.pressed} style={btn}>{extra.icon}</button></div>}
    </div>
  );
}
